import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendGuestSupportForm } from '../../lib/use-cases/send-guest-support-form';
import { logEvent } from '../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../lib/shared/event-types';
import { asyncValidateEmail, validateEmail } from '../../lib/models/email';
import { useRouting } from './use-routing';
import { buildSupportReason } from './build-support-reason';

function useGuestSupportForm(initialEmail = '') {
    const { t } = useTranslation();
    const { lang } = useRouting();
    const [isPending, setIsPending] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [isFail, setIsFail] = useState(false);
    const [fields, setFields] = useState({ email: initialEmail, message: '' });
    const [errors, setErrors] = useState({ email: '', message: '' });

    const changeField = useCallback((name, value) => {
        if (errors[name]) {
            setErrors(prevState => ({ ...prevState, [name]: '' }));
        }

        setFields(prevState => ({ ...prevState, [name]: value }));
    }, [errors]);

    const submitForm = async (e) => {
        e.preventDefault();

        const initialErrors = {};

        try {
            setIsPending(true);

            const emailValidation = validateEmail(fields.email);

            if (!emailValidation.isValid) {
                initialErrors.email = t(`validation.email.${emailValidation.code}`);
            } else {
                const asyncEmailValidation = await asyncValidateEmail(fields.email);

                if (!asyncEmailValidation.isValid) {
                    initialErrors.email = asyncEmailValidation.code === 'didYouMean'
                        ? t('validation.email.didYouMean', { suggestion: asyncEmailValidation.data.didYouMean })
                        : t('validation.email.invalidEmail');
                }
            }

            if (!fields.message.trim()) {
                initialErrors.message = t('validation.form.messageEmpty');
            }

            if (Object.keys(initialErrors).length > 0) {
                return setErrors(prevState => ({ ...prevState, ...initialErrors }));
            }

            logEvent(EVENT_TYPES.SUPPORT_SEND_CLICKED);

            await sendGuestSupportForm({ email: fields.email, message: fields.message, reason: buildSupportReason('Need help Sign in Page', lang) });

            setIsSuccessful(true);
        } catch {
            setIsFail(true);
            logEvent(EVENT_TYPES.SUPPORT_ERROR_VIEWED);
        } finally {
            setIsPending(false);
        }
    };

    const resetForm = () => {
        setIsFail(false);
        setIsSuccessful(false);
        setErrors({ email: '', message: '' });
        setFields({ email: initialEmail, message: '' });
    };

    return {
        fields,
        errors,
        isPending,
        isSuccessful,
        isFail,
        submitForm,
        resetForm,
        changeField
    };
}

export {
    useGuestSupportForm
};
